<template>
  <b-row>
    <div class="col-12">
      <div class="header">
        <div class="header-body">
          <b-row class="align-items-end">
            <div class="col">
              <h1 class="header-title">
                Client Configuration
              </h1>
            </div>
            <div class="col-auto">
              <b-button
                variant="outline-secondary"
                class="mr-2"
                :to="{ name: 'Admin.Brands.Manage', params: { id: id } }"
              >
                <i class="fa fa-arrow-left mr-1"></i>
                Back to Client
              </b-button>
              <b-button variant="primary" @click="SaveForm" :disabled="savingItem">
                <span v-if="savingItem">Saving...</span>
                <span v-else><i class="fa fa-save mr-1"></i>Save Config</span>
              </b-button>
            </div>
          </b-row>
        </div>
      </div>

      <b-tabs>

        <!-- Billing Config: Global -->
        <b-tab title="Billing Config: Global">
          <div v-if="itemForm.clientBillingConfiguration" class="pt-3">

            <!-- Invoice Splitting -->
            <b-card class="mb-3" header="Invoice Splitting" no-body>
              <div class="card-body">
                <b-row>
                  <b-col cols="12">
                    <b-form-group description="Invoice These Country Codes Separately. Separate codes with comma"
                    label="Split Invoice Country Codes:">
                      <b-form-input
                        id="form-input-clientBillingConfiguration-invoiceSplitCountryCodes"
                        name="form-input-clientBillingConfiguration-invoiceSplitCountryCodes"
                        type="text"
                        v-model="itemForm.clientBillingConfiguration.invoiceSplitCountryCodes"
                        v-validate="{ required: false }"
                        />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-card>

            <!-- Storage Fees -->
            <b-card class="mb-3" header="Storage Fees" no-body>
              <div class="card-body">
                <b-row>
                  <b-col cols="12">
                    <b-form-group label="How often to bill recurring storage and pick:">
                      <b-form-select
                        v-model="itemForm.clientBillingConfiguration.howOftenToBillStorageAndPick"
                        :options="howOftenRecurringGenOpts"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group description="Applied to all storage locations, with the minimum items in them."
                    label="Per Pallet Storage Location Fee:">
                      <money
                        id="form-input-clientBillingConfiguration-storageCostPerPallet"
                        name="form-input-clientBillingConfiguration-storageCostPerPallet"
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.storageCostPerPallet"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group description="Pallets who have less then this number will not be billed for."
                      label="Storage Fee - Minimum Items on Pallet:">
                      <b-form-input
                        id="form-input-clientBillingConfiguration-storageCostMinimumItems"
                        name="form-input-clientBillingConfiguration-storageCostMinimumItems"
                        type="number"
                        v-model="itemForm.clientBillingConfiguration.storageCostMinimumItems"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group description="Applied to all pick bins (pick locations), with the minimum items in them."
                    label="Per Pallet Pick Location Fee:">
                      <money
                        id="form-input-clientBillingConfiguration-pickBinCost"
                        name="form-input-clientBillingConfiguration-pickBinCost"
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.pickBinCost"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group description="Pick Bins who have less then this number will not be billed for. Leave 0 for no limit."
                      label="Pick Bin - Maximum Items on Pallet:">
                      <b-form-input
                        id="form-input-clientBillingConfiguration-pickBinCostMaximumItems"
                        name="form-input-clientBillingConfiguration-pickBinCostMaximumItems"
                        type="number"
                        v-model="itemForm.clientBillingConfiguration.pickBinCostMaximumItems"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group>
                      <b-form-checkbox
                        v-model="itemForm.clientBillingConfiguration.storageIgnoreLocationsWithoutPalletId"
                        name="check-button-storageIgnoreLocationsWithoutPalletId"
                        switch
                      >
                        Ignore Storage Locations Without Pallet ID
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group>
                      <b-form-checkbox
                        v-model="itemForm.clientBillingConfiguration.pickIgnoreLocationsWithoutPalletId"
                        name="check-button-pickIgnoreLocationsWithoutPalletId"
                        switch
                      >
                        Ignore Pick Locations Without Pallet ID
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group>
                      <b-form-checkbox
                        v-model="itemForm.clientBillingConfiguration.storageOthersBillAsPick"
                        name="check-button-storageOthersBillAsPick"
                        switch
                      >
                        Pallets in Storage not in set item range, bill as Pick Bins
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group>
                      <b-form-checkbox
                        v-model="itemForm.clientBillingConfiguration.pickOthersBillAsStorage"
                        name="check-button-pickOthersBillAsStorage"
                        switch
                      >
                        Bill Pick Bins not in set item range, as Storage
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-card>

            <!-- Order Return Fees -->
            <b-card class="mb-3" header="Order Return Fees" no-body>
              <div class="card-body">
                <b-row>
                  <b-col cols="6">
                    <b-form-group description="Only applied IF the Order Return is received into the WMS, that is, if returned at least one item into inventory."
                    label="Fee for Processing an Order Return (in USD):">
                      <money
                        id="form-input-clientBillingConfiguration-orderReturnSingleCharge"
                        name="form-input-clientBillingConfiguration-orderReturnSingleCharge"
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.orderReturnSingleCharge"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group description="Applied for each UNIT of an item returned. If 3 units of SKU EXAMPLE_SKU were returned for a given order return, the value below is multiplied by 3 when totaling the charge."
                    label="Fee charged for Order Item Returned (in USD):">
                      <money
                        id="form-input-clientBillingConfiguration-orderReturnItemSingleCharge"
                        name="form-input-clientBillingConfiguration-orderReturnItemSingleCharge"
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.orderReturnItemSingleCharge"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                </b-row>
                <hr />
                <b-row>
                  <b-col cols="12">
                    <b-form-group>
                      <b-form-checkbox
                        v-model="itemForm.clientBillingConfiguration.allowClientToGenerateReturnLabels"
                        name="check-button-allowClientToGenerateReturnLabels"
                        switch
                      >
                        Allow Client to Generate Return Labels
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row v-if="itemForm.clientBillingConfiguration.allowClientToGenerateReturnLabels">
                  <b-col cols="6">
                    <b-form-group label="Return Label Carrier:" description="Only eHub carriers are supported for client-generated return labels.">
                      <b-v-select
                        v-model="itemForm.clientBillingConfiguration.integrationCarrierId"
                        :searchable="true"
                        :options="allowedReturnCarrierOptions"
                        placeholder="Select carrier..."
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group label="Return Label Billing Method:">
                      <b-form-select
                        v-model="itemForm.clientBillingConfiguration.returnLabelBillingMethod"
                        :options="returnLabelBillingMethodOpts"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6" v-if="itemForm.clientBillingConfiguration.returnLabelBillingMethod === 'UsingPercentageMarkup'">
                    <b-form-group label="Percentage Markup Amount (%):" description="Percentage added on top of the carrier rate.">
                      <b-form-input
                        type="number"
                        v-model="itemForm.clientBillingConfiguration.percentageMarkupAmount"
                        min="0"
                        step="0.01"
                        />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6" v-if="itemForm.clientBillingConfiguration.returnLabelBillingMethod === 'UsingFixedAmountMarkup'">
                    <b-form-group label="Fixed Amount Markup (USD):" description="Fixed dollar amount added on top of the carrier rate.">
                      <money
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.fixedAmountMarkup"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6" v-if="itemForm.clientBillingConfiguration.returnLabelBillingMethod === 'UsingFixedCostPerLabel'">
                    <b-form-group label="Fixed Label Fee (USD):" description="Fixed cost charged to client per return label generated.">
                      <money
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.fixedLabelFee"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-card>

            <!-- Fulfillment Service Fees -->
            <b-card class="mb-3" header="Fulfillment Service Fees" no-body>
              <div class="card-body">
                <b-alert variant="warning" show class="mb-3"><b>Important:</b> You should only configure the values here, if you did NOT have it set in Extensiv, otherwise you'll be billing the client twice for the same service offered.</b-alert>
                <b-row>
                  <b-col cols="6">
                    <b-form-group description="Applied to each order after it is fulfilled."
                    label="Order Processing Fulfillment Fee (USD):">
                      <money
                        id="form-input-clientBillingConfiguration-fulfillmentOrderProcessingFee"
                        name="form-input-clientBillingConfiguration-fulfillmentOrderProcessingFee"
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.fulfillmentOrderProcessingFee"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group description="Applied to each shipment/parcel/box that ships. Can also be called a 'per label' fee."
                    label="Shipment/Parcel (Label) Fulfillment Fee (USD):">
                      <money
                        id="form-input-clientBillingConfiguration-fulfillmentShipmentProcessingFee"
                        name="form-input-clientBillingConfiguration-fulfillmentShipmentProcessingFee"
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.fulfillmentShipmentProcessingFee"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group description="Applied to each unit picked and packaged. If multiple units of the same SKU are being fulfilled, each unit is counted separately."
                    label="Per Unit Fulfillment Fee (USD):">
                      <money
                        id="form-input-clientBillingConfiguration-fulfillmentUnitProcessingFee"
                        name="form-input-clientBillingConfiguration-fulfillmentUnitProcessingFee"
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.fulfillmentUnitProcessingFee"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        :disabled="itemForm.clientBillingConfiguration.fulfillmentUnitUniqueProcessingFee > 0"
                        />
                      <small v-if="itemForm.clientBillingConfiguration.fulfillmentUnitUniqueProcessingFee > 0" class="text-muted">Disabled — Unique Unit Fee is set.</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group description="Applied to each unique product SKU being picked and packaged. If multiple units of the same SKU are being fulfilled, the fee is being applied ONCE. NOTE: Do not set this fee and the per unit fee at the same time. You can only set one of them."
                    label="Unique Unit (Sku) Fulfillment Fee (USD):">
                      <money
                        id="form-input-clientBillingConfiguration-fulfillmentUnitUniqueProcessingFee"
                        name="form-input-clientBillingConfiguration-fulfillmentUnitUniqueProcessingFee"
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.fulfillmentUnitUniqueProcessingFee"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        :disabled="itemForm.clientBillingConfiguration.fulfillmentUnitProcessingFee > 0"
                        />
                      <small v-if="itemForm.clientBillingConfiguration.fulfillmentUnitProcessingFee > 0" class="text-muted">Disabled — Per Unit Fee is set.</small>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-card>

            <!-- Administration Fees -->
            <b-card class="mb-3" header="Administration Fees" no-body>
              <div class="card-body">
                <b-row>
                  <b-col cols="6">
                    <b-form-group description="Applied to ONE invoice, on the 1st of each month. If the client is shipping from multiple warehouse, they will only be billed this on the first warehouse in the list, defaults to McKinney invoice."
                    label="Monthly Recurring Admin Fee (in USD):">
                      <money
                        id="form-input-clientBillingConfiguration-monthlyAdminFee"
                        name="form-input-clientBillingConfiguration-monthlyAdminFee"
                        class="form-control"
                        v-model="itemForm.clientBillingConfiguration.monthlyAdminFee"
                        v-validate="{ required: true, min: 0 }"
                        min="0"
                        />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-card>

          </div>
          <div v-else class="text-muted pt-3">No billing configuration available for this client.</div>
        </b-tab>

        <!-- Billing Config: Per Warehouse -->
        <b-tab title="Billing Config: Per Warehouse">
          <div class="pt-3">

            <b-card class="mb-3" no-body>
              <div class="card-body">
                <b-row class="align-items-end">
                  <b-col cols="12" md="5">
                    <b-form-group label="Select Warehouse:" class="mb-0">
                      <b-v-select
                        v-model="selectedPerWarehouseId"
                        :searchable="true"
                        :options="warehousesList"
                        placeholder="Pick a warehouse to configure..."
                        @input="onWarehouseSelected"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col v-if="selectedPerWarehouseId && !selectedWarehouseConfig" cols="12" md="3">
                    <b-button variant="success" @click="addWarehouseConfig">
                      <i class="fa fa-plus mr-1"></i>Add Config for this Warehouse
                    </b-button>
                  </b-col>
                  <b-col v-if="selectedPerWarehouseId && selectedWarehouseConfig" cols="12" md="3">
                    <b-button variant="outline-danger" size="sm" @click="removeWarehouseConfig">
                      <i class="fa fa-trash mr-1"></i>Remove Warehouse Config
                    </b-button>
                  </b-col>
                </b-row>

                <div v-if="itemForm.clientBillingConfigByWarehouse && itemForm.clientBillingConfigByWarehouse.length > 0" class="mt-3">
                  <div class="text-muted small mb-1">Configured warehouses:</div>
                  <b-button-group>
                    <b-button
                      v-for="whConfig in itemForm.clientBillingConfigByWarehouse"
                      :key="whConfig.warehouseId"
                      :variant="selectedPerWarehouseId === whConfig.warehouseId ? 'primary' : 'outline-primary'"
                      size="sm"
                      @click="selectedPerWarehouseId = whConfig.warehouseId"
                    >
                      {{ getWarehouseName(whConfig.warehouseId) }}
                    </b-button>
                  </b-button-group>
                </div>
              </div>
            </b-card>

            <div v-if="!selectedPerWarehouseId" class="text-muted">
              Select a warehouse above to view or edit its billing configuration.
            </div>

            <div v-else-if="!selectedWarehouseConfig" class="text-muted">
              No configuration exists for this warehouse yet. Click "Add Config" to create one.
            </div>

            <div v-else>

              <!-- Invoice Splitting -->
              <b-card class="mb-3" header="Invoice Splitting" no-body>
                <div class="card-body">
                  <b-row>
                    <b-col cols="12">
                      <b-form-group description="Invoice These Country Codes Separately. Separate codes with comma"
                      label="Split Invoice Country Codes:">
                        <b-form-input
                          type="text"
                          v-model="selectedWarehouseConfig.invoiceSplitCountryCodes"
                          />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </b-card>

              <!-- Storage Fees -->
              <b-card class="mb-3" header="Storage Fees" no-body>
                <div class="card-body">
                  <b-row>
                    <b-col cols="12">
                      <b-form-group label="How often to bill recurring storage and pick:">
                        <b-form-select
                          v-model="selectedWarehouseConfig.howOftenToBillStorageAndPick"
                          :options="howOftenRecurringGenOpts"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group description="Applied to all storage locations, with the minimum items in them."
                      label="Per Pallet Storage Location Fee:">
                        <money
                          class="form-control"
                          v-model="selectedWarehouseConfig.storageCostPerPallet"
                          min="0"
                          />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group description="Pallets who have less then this number will not be billed for."
                        label="Storage Fee - Minimum Items on Pallet:">
                        <b-form-input
                          type="number"
                          v-model="selectedWarehouseConfig.storageCostMinimumItems"
                          min="0"
                          />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group description="Applied to all pick bins (pick locations), with the minimum items in them."
                      label="Per Pallet Pick Location Fee:">
                        <money
                          class="form-control"
                          v-model="selectedWarehouseConfig.pickBinCost"
                          min="0"
                          />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group description="Pick Bins who have less then this number will not be billed for. Leave 0 for no limit."
                        label="Pick Bin - Maximum Items on Pallet:">
                        <b-form-input
                          type="number"
                          v-model="selectedWarehouseConfig.pickBinCostMaximumItems"
                          min="0"
                          />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group>
                        <b-form-checkbox
                          v-model="selectedWarehouseConfig.storageIgnoreLocationsWithoutPalletId"
                          switch
                        >
                          Ignore Storage Locations Without Pallet ID
                        </b-form-checkbox>
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group>
                        <b-form-checkbox
                          v-model="selectedWarehouseConfig.pickIgnoreLocationsWithoutPalletId"
                          switch
                        >
                          Ignore Pick Locations Without Pallet ID
                        </b-form-checkbox>
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group>
                        <b-form-checkbox
                          v-model="selectedWarehouseConfig.storageOthersBillAsPick"
                          switch
                        >
                          Pallets in Storage not in set item range, bill as Pick Bins
                        </b-form-checkbox>
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group>
                        <b-form-checkbox
                          v-model="selectedWarehouseConfig.pickOthersBillAsStorage"
                          switch
                        >
                          Bill Pick Bins not in set item range, as Storage
                        </b-form-checkbox>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </b-card>

              <!-- Order Return Fees -->
              <b-card class="mb-3" header="Order Return Fees" no-body>
                <div class="card-body">
                  <b-row>
                    <b-col cols="6">
                      <b-form-group description="Only applied IF the Order Return is received into the WMS, that is, if returned at least one item into inventory."
                      label="Fee for Processing an Order Return (in USD):">
                        <money
                          class="form-control"
                          v-model="selectedWarehouseConfig.orderReturnSingleCharge"
                          min="0"
                          />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group description="Applied for each UNIT of an item returned. If 3 units of SKU EXAMPLE_SKU were returned for a given order return, the value below is multiplied by 3 when totaling the charge."
                      label="Fee charged for Order Item Returned (in USD):">
                        <money
                          class="form-control"
                          v-model="selectedWarehouseConfig.orderReturnItemSingleCharge"
                          min="0"
                          />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <hr />
                  <b-row>
                    <b-col cols="12">
                      <b-form-group>
                        <b-form-checkbox
                          v-model="selectedWarehouseConfig.allowClientToGenerateReturnLabels"
                          switch
                        >
                          Allow Client to Generate Return Labels
                        </b-form-checkbox>
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row v-if="selectedWarehouseConfig.allowClientToGenerateReturnLabels">
                    <b-col cols="6">
                      <b-form-group label="Return Label Carrier (eHub):" description="Only eHub carriers are supported for client-generated return labels.">
                        <b-v-select
                          v-model="selectedWarehouseConfig.integrationCarrierId"
                          :searchable="true"
                          :options="allowedReturnCarrierOptions"
                          placeholder="Select carrier..."
                        />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group label="Return Label Billing Method:">
                        <b-form-select
                          v-model="selectedWarehouseConfig.returnLabelBillingMethod"
                          :options="returnLabelBillingMethodOpts"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6" v-if="selectedWarehouseConfig.returnLabelBillingMethod === 'UsingPercentageMarkup'">
                      <b-form-group label="Percentage Markup Amount (%):" description="Percentage added on top of the carrier rate.">
                        <b-form-input
                          type="number"
                          v-model="selectedWarehouseConfig.percentageMarkupAmount"
                          min="0"
                          step="0.01"
                          />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6" v-if="selectedWarehouseConfig.returnLabelBillingMethod === 'UsingFixedAmountMarkup'">
                      <b-form-group label="Fixed Amount Markup (USD):" description="Fixed dollar amount added on top of the carrier rate.">
                        <money
                          class="form-control"
                          v-model="selectedWarehouseConfig.fixedAmountMarkup"
                          min="0"
                          />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6" v-if="selectedWarehouseConfig.returnLabelBillingMethod === 'UsingFixedCostPerLabel'">
                      <b-form-group label="Fixed Label Fee (USD):" description="Fixed cost charged to client per return label generated.">
                        <money
                          class="form-control"
                          v-model="selectedWarehouseConfig.fixedLabelFee"
                          min="0"
                          />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </b-card>

              <!-- Fulfillment Service Fees -->
              <b-card class="mb-3" header="Fulfillment Service Fees" no-body>
                <div class="card-body">
                <b-alert variant="warning" show class="mb-3"><b>Important:</b> You should only configure the values here, if you did NOT have it set in Extensiv, otherwise you'll be billing the client twice for the same service offered.</b-alert>
                  <b-row>
                    <b-col cols="6">
                      <b-form-group description="Applied to each order after it is fulfilled."
                      label="Order Processing Fulfillment Fee (USD):">
                        <money
                          class="form-control"
                          v-model="selectedWarehouseConfig.fulfillmentOrderProcessingFee"
                          min="0"
                          />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group description="Applied to each shipment/parcel/box that ships. Can also be called a 'per label' fee."
                      label="Shipment/Parcel (Label) Fulfillment Fee (USD):">
                        <money
                          class="form-control"
                          v-model="selectedWarehouseConfig.fulfillmentShipmentProcessingFee"
                          min="0"
                          />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group description="Applied to each unit picked and packaged. If multiple units of the same SKU are being fulfilled, each unit is counted separately."
                      label="Per Unit Fulfillment Fee (USD):">
                        <money
                          class="form-control"
                          v-model="selectedWarehouseConfig.fulfillmentUnitProcessingFee"
                          min="0"
                          :disabled="selectedWarehouseConfig.fulfillmentUnitUniqueProcessingFee > 0"
                          />
                        <small v-if="selectedWarehouseConfig.fulfillmentUnitUniqueProcessingFee > 0" class="text-muted">Disabled — Unique Unit Fee is set.</small>
                      </b-form-group>
                    </b-col>
                    <b-col cols="6">
                      <b-form-group description="Applied to each unique product SKU being picked and packaged. If multiple units of the same SKU are being fulfilled, the fee is being applied ONCE. NOTE: Do not set this fee and the per unit fee at the same time. You can only set one of them."
                      label="Unique Unit (Sku) Fulfillment Fee (USD):">
                        <money
                          class="form-control"
                          v-model="selectedWarehouseConfig.fulfillmentUnitUniqueProcessingFee"
                          min="0"
                          :disabled="selectedWarehouseConfig.fulfillmentUnitProcessingFee > 0"
                          />
                        <small v-if="selectedWarehouseConfig.fulfillmentUnitProcessingFee > 0" class="text-muted">Disabled — Per Unit Fee is set.</small>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </b-card>
            </div>
          </div>
        </b-tab>

        <!-- Carrier Integration Tab -->
        <b-tab title="Carrier Integration" lazy @click="loadIntegrationCarriers">
          <b-card class="mb-3 mt-3" header="Carrier Integration Configuration" no-body>
            <div class="card-body">
              <div class="items">
                <b-table
                  head-variant="light"
                  :fields="carrierConfigFields"
                  :items="itemForm.carrierIntegrationConfiguration ? itemForm.carrierIntegrationConfiguration.items : []"
                  :bordered="true"
                  responsive
                >
                  <template v-slot:cell(integrationCarrierId)="data">
                    <span v-if="integrationCarriers.find(c => c.id === data.item.integrationCarrierId)">
                      {{ integrationCarriers.find(c => c.id === data.item.integrationCarrierId).name }}
                    </span>
                    <span v-else>{{ data.item.integrationCarrierId }}</span>
                  </template>
                  <template v-slot:cell(warehouseId)="data">
                    <span v-if="myWarehouses && myWarehouses.find(w => w.id === data.item.warehouseId)">
                      {{ myWarehouses.find(w => w.id === data.item.warehouseId).name }}
                    </span>
                    <span v-else>{{ data.item.warehouseId }}</span>
                  </template>
                  <template v-slot:cell(actions)="data">
                    <div class="d-flex">
                      <button
                        class="btn btn-danger ml-1"
                        @click="RemoveCarrierIntegrationConfig(data.item)"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </template>
                </b-table>
              </div>
            </div>
            <template v-slot:footer>
              <div v-if="itemForm.id" class="form-row">
                <div class="col-12 col-md-3">
                  <b-form-group label="Integration Carrier">
                    <b-v-select
                      :showup="true"
                      v-model="carrierIntegrationConfigurationForm.integrationCarrierId"
                      :searchable="true"
                      :options="integrationCarrierOptions"
                      placeholder="Select carrier..."
                    />
                  </b-form-group>
                </div>
                <div class="col-12 col-md-3">
                  <b-form-group label="Warehouse">
                    <b-v-select
                      :showup="true"
                      v-model="carrierIntegrationConfigurationForm.warehouseId"
                      :searchable="true"
                      :options="warehousesList"
                      placeholder="Select warehouse..."
                    />
                  </b-form-group>
                </div>
                <div class="col-12 col-md-2">
                  <b-form-group label=".">
                    <b-button
                      type="button"
                      block
                      variant="primary"
                      @click="AddCarrierConfig()"
                    >
                      Add
                    </b-button>
                  </b-form-group>
                </div>
              </div>
            </template>
          </b-card>
        </b-tab>

        <!-- Order Routing Tab -->
        <b-tab title="Order Routing" lazy>
          <storefront-order-routing-config
            v-model="itemForm.storefrontRoutingConfig"
            :brand-id="id"
          />
        </b-tab>

      </b-tabs>
    </div>
  </b-row>
</template>

<script>
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import StorefrontOrderRoutingConfig from './StorefrontOrderRoutingConfig.vue';
export default {
  name: 'Admin.Brands.Config',
  components: { StorefrontOrderRoutingConfig },
  mixins: [crudMixinGen('brands'), myCommonDataMixin],
  props: {
    id: {
      type: String,
      default: '',
      required: false
    }
  },

  data: () => ({
    selectedPerWarehouseId: '',
    integrationCarriers: [],
    carrierIntegrationConfigurationForm: {
      integrationCarrierId: '',
      warehouseId: '',
      filterByStorefrontId: '',
      filterByOrderGroupId: ''
    },
    carrierConfigFields: [
      { key: 'integrationCarrierId', label: 'Integration Carrier', sortable: false },
      { key: 'warehouseId', label: 'Warehouse', sortable: false },
      { key: 'actions', label: 'Actions', sortable: false }
    ],
    howOftenRecurringGenOpts: [
      { value: 'Weekly', text: 'Weekly - Every Monday' },
      { value: 'Monthly', text: 'Monthly - On the 1st' }
    ],
    returnLabelBillingMethodOpts: [
      { value: 'UsingRateCards', text: 'Using Rate Cards' },
      { value: 'UsingFixedCostPerLabel', text: 'Using Fixed Cost Per Label' },
      { value: 'UsingPercentageMarkup', text: 'Using Percentage Markup' },
      { value: 'UsingFixedAmountMarkup', text: 'Using Fixed Amount Markup' }
    ]
  }),

  watch: {
    'itemForm.clientBillingConfiguration.fulfillmentUnitProcessingFee': function(val) {
      if (val > 0 && this.itemForm.clientBillingConfiguration) {
        this.itemForm.clientBillingConfiguration.fulfillmentUnitUniqueProcessingFee = 0;
      }
    },
    'itemForm.clientBillingConfiguration.fulfillmentUnitUniqueProcessingFee': function(val) {
      if (val > 0 && this.itemForm.clientBillingConfiguration) {
        this.itemForm.clientBillingConfiguration.fulfillmentUnitProcessingFee = 0;
      }
    },
    'selectedWarehouseConfig.fulfillmentUnitProcessingFee': function(val) {
      if (val > 0 && this.selectedWarehouseConfig) {
        this.selectedWarehouseConfig.fulfillmentUnitUniqueProcessingFee = 0;
      }
    },
    'selectedWarehouseConfig.fulfillmentUnitUniqueProcessingFee': function(val) {
      if (val > 0 && this.selectedWarehouseConfig) {
        this.selectedWarehouseConfig.fulfillmentUnitProcessingFee = 0;
      }
    }
  },

  computed: {
    integrationCarrierOptions() {
      return this.integrationCarriers.map(carrier => ({
        value: carrier.id,
        text: carrier.name + (carrier.description ? ' - ' + carrier.description : '')
      }));
    },
    allowedReturnCarrierOptions() {
      return this.integrationCarriers
        .filter(function(c) { return c.processingSystem === 'eHub' || c.processingSystem === 'ShipEngine'; })
        .map(function(c) {
          return { value: c.id, text: c.name + (c.description ? ' - ' + c.description : '') };
        });
    },
    selectedWarehouseConfig() {
      if (!this.selectedPerWarehouseId || !this.itemForm.clientBillingConfigByWarehouse) {
        return null;
      }
      return this.itemForm.clientBillingConfigByWarehouse.find(
        c => c.warehouseId === this.selectedPerWarehouseId
      ) || null;
    }
  },

  mounted() {
    this.$store.dispatch('fetchMyTPLData');
    this.loadIntegrationCarriers();
    if (this.id) {
      this.ResetForm();
      this.LoadItem(this.id)
        .then(() => {
          if (!this.itemForm.clientBillingConfigByWarehouse) {
            this.$set(this.itemForm, 'clientBillingConfigByWarehouse', []);
          }
          if (this.itemForm.carrierIntegrationConfiguration && this.itemForm.carrierIntegrationConfiguration.items) {
            this.$set(this.itemForm.carrierIntegrationConfiguration, 'items', this.itemForm.carrierIntegrationConfiguration.items);
          } else {
            this.$set(this.itemForm, 'carrierConfig', { items: [] });
          }
        });
    } else {
      this.ResetForm();
    }
  },

  methods: {
    getWarehouseName(warehouseId) {
      var wh = this.myWarehouses && this.myWarehouses.find(function(w) { return w.id === warehouseId; });
      return wh ? wh.name : warehouseId;
    },
    onWarehouseSelected() {
      // no-op — selectedWarehouseConfig computed handles the lookup reactively
    },
    addWarehouseConfig() {
      if (!this.selectedPerWarehouseId) return;
      this.itemForm.clientBillingConfigByWarehouse.push({
        warehouseId: this.selectedPerWarehouseId,
        invoiceSplitCountryCodes: '',
        howOftenToBillStorageAndPick: 'Monthly',
        storageCostPerPallet: 0,
        storageCostMinimumItems: 0,
        pickBinCost: 0,
        pickBinCostMaximumItems: 0,
        storageIgnoreLocationsWithoutPalletId: false,
        pickIgnoreLocationsWithoutPalletId: false,
        storageOthersBillAsPick: false,
        pickOthersBillAsStorage: false,
        orderReturnSingleCharge: 0,
        orderReturnItemSingleCharge: 0,
        allowClientToGenerateReturnLabels: false,
        integrationCarrierId: null,
        returnLabelBillingMethod: 'UsingRateCards',
        percentageMarkupAmount: 0,
        fixedAmountMarkup: 0,
        fixedLabelFee: 0,
        fulfillmentOrderProcessingFee: 0,
        fulfillmentShipmentProcessingFee: 0,
        fulfillmentUnitProcessingFee: 0,
        fulfillmentUnitUniqueProcessingFee: 0,
        monthlyAdminFee: 0
      });
    },
    removeWarehouseConfig() {
      if (!this.selectedPerWarehouseId) return;
      this.itemForm.clientBillingConfigByWarehouse = this.itemForm.clientBillingConfigByWarehouse.filter(
        c => c.warehouseId !== this.selectedPerWarehouseId
      );
    },
    loadIntegrationCarriers() {
      if (this.integrationCarriers.length > 0) {
        return;
      }
      this.$http
        .post('/api/manage/common/integrationCarriers/listItems', {
          perPage: 100,
          showAll: true
        })
        .then((resp) => {
          this.integrationCarriers = resp.data.items || [];
        })
        .catch((err) => {
          console.error('Failed to load integration carriers', err);
        });
    },
    AddCarrierConfig() {
      if (!this.carrierIntegrationConfigurationForm.integrationCarrierId) {
        this.$swal('Error', 'Please select an Integration Carrier', 'error');
        return;
      }
      if (!this.carrierIntegrationConfigurationForm.warehouseId) {
        this.$swal('Error', 'Please select a Warehouse', 'error');
        return;
      }
      const isDuplicate = this.itemForm.carrierIntegrationConfiguration.items.some(item =>
        item.integrationCarrierId === this.carrierIntegrationConfigurationForm.integrationCarrierId &&
        item.warehouseId === this.carrierIntegrationConfigurationForm.warehouseId
      );
      if (isDuplicate) {
        this.$swal('Error', 'This Integration Carrier and Warehouse combination already exists', 'error');
        return;
      }
      this.itemForm.carrierIntegrationConfiguration.items.push({
        integrationCarrierId: this.carrierIntegrationConfigurationForm.integrationCarrierId,
        warehouseId: this.carrierIntegrationConfigurationForm.warehouseId,
        filterByStorefrontId: this.carrierIntegrationConfigurationForm.filterByStorefrontId || '',
        filterByOrderGroupId: this.carrierIntegrationConfigurationForm.filterByOrderGroupId || ''
      });
      this.carrierIntegrationConfigurationForm = {
        integrationCarrierId: '',
        warehouseId: '',
        filterByStorefrontId: '',
        filterByOrderGroupId: ''
      };
    },
    RemoveCarrierIntegrationConfig(item) {
      this.itemForm.carrierIntegrationConfiguration.items =
        this.itemForm.carrierIntegrationConfiguration.items.filter(config =>
          !(config.integrationCarrierId === item.integrationCarrierId &&
            config.warehouseId === item.warehouseId)
        );
    },
    SaveForm() {
      this.savingItem = true;
      this.SaveItem()
        .then(() => {
          this.$swal({ title: 'Config Saved!', icon: 'success' });
        })
        .catch(err => {
          this.savingItem = false;
          if (err.response && err.response.data && err.response.data.errorMessage) {
            this.$swal({ title: err.response.data.errorMessage, icon: 'error' });
          } else {
            this.$swal({ title: 'Config Was NOT Saved!', icon: 'error' });
          }
        });
    }
  }
};
</script>
